// ─── AKSAPÊ ENTERPRISE — SHARED CONSTANTS ────────────────────────────────────

export const APP_NAME = 'AKSAPÊ Enterprise';
export const APP_VERSION = '1.0.0';

// ── API ───────────────────────────────────────────────────────────────────────

export const API_VERSION = 'v1';
export const API_BASE_PATH = `/api/${API_VERSION}`;

// Pagination
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

// Rate limiting
export const RATE_LIMIT_WINDOW_MS = 60 * 1000; // 1 minute
export const RATE_LIMIT_MAX_REQUESTS = 100;
export const RATE_LIMIT_API_KEY_MAX = 1000; // per hour

// ── API KEYS (Sprint 3.11 — API Keys real) ──────────────────────────────────
// Escopos válidos pra ApiKey (model já existia, mas nada validava a chave
// antes desta sprint — ver apps/api/src/shared/middleware/api-key-auth.ts).
// Cada chave só pode ser usada pro que foi concedida na criação, nunca
// "tudo liberado" — mesmo raciocínio de RECURSOS/PerfilPermissao pra
// usuários da equipe. Crescer aqui conforme novos endpoints públicos
// ganham suporte a autenticação por API Key.
export const API_KEY_ESCOPOS = {
  LEADS_ESCRITA: 'leads:escrita',
} as const;

export type ApiKeyEscopo = (typeof API_KEY_ESCOPOS)[keyof typeof API_KEY_ESCOPOS];

export const API_KEY_ESCOPO_LABELS: Record<ApiKeyEscopo, string> = {
  [API_KEY_ESCOPOS.LEADS_ESCRITA]: 'Enviar leads (portal/CRM de parceiro)',
};

// ── AUTH ──────────────────────────────────────────────────────────────────────

export const JWT_EXPIRES_IN = '1h';
export const REFRESH_TOKEN_EXPIRES_IN = '7d';
export const MAGIC_LINK_EXPIRES_IN = '15m';

// ── UPLOAD ───────────────────────────────────────────────────────────────────

export const MAX_UPLOAD_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
export const MAX_UPLOAD_SIZE_MB = 50;
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];

// Storage buckets
export const BUCKET_IMOVEIS_FOTOS = 'imoveis-fotos';
export const BUCKET_DOCUMENTOS = 'documentos';
export const BUCKET_AVATARES = 'avatares';
export const BUCKET_LOGOS = 'logos';

// ── SUPABASE ─────────────────────────────────────────────────────────────────

export const SUPABASE_BUCKETS = [
  BUCKET_IMOVEIS_FOTOS,
  BUCKET_DOCUMENTOS,
  BUCKET_AVATARES,
  BUCKET_LOGOS,
] as const;

// ── BUSINESS RULES ────────────────────────────────────────────────────────────

// Leads
export const LEAD_PARADO_ALERTA_DIAS = 3;
export const LEAD_PARADO_REDISTRIBUIR_DIAS = 7;
export const LEAD_ARQUIVAR_PERDIDO_DIAS = 365;

// Locações
export const LOCACAO_DIA_VENCIMENTO_PADRAO = 5;
export const LOCACAO_MULTA_ATRASO_PERCENTUAL = 2; // 2%
export const LOCACAO_JUROS_MES_PERCENTUAL = 1; // 1% a.m.
export const LOCACAO_AVISO_RENOVACAO_DIAS = 90;
export const LOCACAO_PERCENTUAL_ADMIN_PADRAO = 10; // 10%

// Vendas
export const VENDA_COMISSAO_PADRAO_PERCENTUAL = 6; // 6%

// ── ETAPAS LEAD ───────────────────────────────────────────────────────────────

export const ETAPAS_LEAD_LABELS: Record<string, string> = {
  lead: 'Lead',
  contato: 'Contato Feito',
  visita: 'Visita Agendada',
  proposta: 'Proposta Enviada',
  negociacao: 'Negociação',
  fechado: 'Fechado ✓',
  perdido: 'Perdido ✗',
};

export const ETAPAS_LEAD_CORES: Record<string, string> = {
  lead: '#94a3b8',
  contato: '#60a5fa',
  visita: '#a78bfa',
  proposta: '#f59e0b',
  negociacao: '#f97316',
  fechado: '#10b981',
  perdido: '#ef4444',
};

export const ETAPAS_LEAD_ORDEM = [
  'lead',
  'contato',
  'visita',
  'proposta',
  'negociacao',
  'fechado',
  'perdido',
] as const;

// ── PERFIS ────────────────────────────────────────────────────────────────────

export const PERFIS_LABELS: Record<string, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  corretor: 'Corretor',
  juridico: 'Jurídico',
  financeiro: 'Financeiro',
  assistente: 'Assistente',
};

// ── RECURSOS RBAC ─────────────────────────────────────────────────────────────

export const RECURSOS = {
  LEADS: 'leads',
  IMOVEIS: 'imoveis',
  PESSOAS: 'pessoas',
  LOCACOES: 'locacoes',
  VENDAS: 'vendas',
  FINANCEIRO: 'financeiro',
  JURIDICO: 'juridico',
  DOCUMENTOS: 'documentos',
  RELATORIOS: 'relatorios',
  CONFIGURACOES: 'configuracoes',
  USUARIOS: 'usuarios',
  AGENDA: 'agenda',
  WHATSAPP: 'whatsapp',
  IA: 'ia',
  // Sprint 3.1 — Central de Relacionamento Omnichannel. RECURSOS.WHATSAPP
  // (acima) fica intocado/não usado por este módulo — RELACIONAMENTO cobre
  // WhatsApp e todos os demais canais (Pessoa → Canal → Conversa → Mensagem).
  RELACIONAMENTO: 'relacionamento',
  // Sprint 3.5 — Administração do Sistema.
  FILIAIS: 'filiais',
  PERFIS: 'perfis',
  TAGS: 'tags',
  NOTIFICACOES_CONFIG: 'notificacoes_config',
  PERSONALIZACAO: 'personalizacao',
  INTEGRACOES: 'integracoes',
  // Sprint 3.7 — Plataforma de IA, Fase 1. Motor de Automações (construção/
  // publicação de fluxos) é um recurso próprio, distinto de RECURSOS.IA
  // (uso de tools/sessões de IA) — ver docs/MASTER_ARCHITECTURE_IA_AKSAPE.md §14.3.
  AUTOMACOES: 'automacoes',
  // Sprint 3.8 — Numeração Automática (core central de geração de códigos).
  NUMERACAO: 'numeracao',
  // Sprint 3.6, Fase A — Inteligência Comercial / BI. COMERCIAL_BI cobre
  // dashboard/funil/rankings/indicadores/projeções/insights (leitura
  // agregada sobre módulos já existentes); METAS é o único CRUD novo desta
  // fase (cadastro de metas por escopo).
  COMERCIAL_BI: 'comercial_bi',
  METAS: 'metas',
} as const;

export const ACOES = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

// ── RBAC BANCO-DRIVEN (Sprint 3.5) ─────────────────────────────────────────────

// Vocabulário canônico das 7 ações do editor de permissões — mirror de
// AcaoCanonica em auth.ts (apps/api). Ordem = ordem de exibição na matriz.
export const ACOES_CANONICAS = [
  'visualizar',
  'criar',
  'editar',
  'excluir',
  'exportar',
  'imprimir',
  'alterar_responsavel',
] as const;

export const ACOES_CANONICAS_LABELS: Record<(typeof ACOES_CANONICAS)[number], string> = {
  visualizar: 'Visualizar',
  criar: 'Criar',
  editar: 'Editar',
  excluir: 'Excluir',
  exportar: 'Exportar',
  imprimir: 'Imprimir',
  alterar_responsavel: 'Alterar Responsável',
};

export const RECURSOS_LABELS: Record<string, string> = {
  leads: 'Leads',
  imoveis: 'Imóveis',
  pessoas: 'Pessoas',
  locacoes: 'Locações',
  vendas: 'Vendas / Negociações',
  financeiro: 'Financeiro',
  juridico: 'Jurídico',
  documentos: 'Documentos',
  relatorios: 'Relatórios',
  configuracoes: 'Configurações',
  usuarios: 'Usuários',
  agenda: 'Agenda',
  whatsapp: 'WhatsApp',
  ia: 'Inteligência Artificial',
  relacionamento: 'Relacionamento',
  filiais: 'Filiais',
  perfis: 'Perfis',
  tags: 'Tags',
  notificacoes_config: 'Notificações (config.)',
  personalizacao: 'Personalização',
  integracoes: 'Integrações',
  automacoes: 'Automações (IA)',
  numeracao: 'Numeração Automática',
  comercial_bi: 'Inteligência Comercial',
  metas: 'Metas',
};

export const ESCOPO_DADOS_LABELS = {
  none: 'Nenhum',
  own: 'Próprio',
  team: 'Equipe',
  branch: 'Filial',
  global: 'Global',
} as const;

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────

export const DESIGN = {
  colors: {
    navy: '#0D1B3E',
    gold: '#C9A227',
    goldLight: '#E8C050',
    light: '#F4F6FB',
    dark: '#1E293B',
    gray: '#64748B',
    slate: '#475569',
    white: '#FFFFFF',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#60a5fa',
  },
  sidebar: {
    width: '220px',
    widthCollapsed: '72px',
  },
} as const;

// ── ERROR CODES ───────────────────────────────────────────────────────────────

export const ERROR_CODES = {
  // Auth
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  AUTH_INSUFFICIENT_PERMISSIONS: 'AUTH_INSUFFICIENT_PERMISSIONS',
  AUTH_2FA_REQUIRED: 'AUTH_2FA_REQUIRED',
  AUTH_ACCOUNT_DISABLED: 'AUTH_ACCOUNT_DISABLED',

  // Validation
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  VALIDATION_REQUIRED_FIELD: 'VALIDATION_REQUIRED_FIELD',
  VALIDATION_INVALID_CPF: 'VALIDATION_INVALID_CPF',
  VALIDATION_INVALID_CNPJ: 'VALIDATION_INVALID_CNPJ',
  VALIDATION_DUPLICATE_EMAIL: 'VALIDATION_DUPLICATE_EMAIL',

  // Business
  LEAD_NOT_FOUND: 'LEAD_NOT_FOUND',
  IMOVEL_NOT_FOUND: 'IMOVEL_NOT_FOUND',
  PESSOA_NOT_FOUND: 'PESSOA_NOT_FOUND',
  TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  LEAD_PERDIDO_MOTIVO_REQUIRED: 'LEAD_PERDIDO_MOTIVO_REQUIRED',

  // Infrastructure
  UPLOAD_TOO_LARGE: 'UPLOAD_TOO_LARGE',
  UPLOAD_INVALID_TYPE: 'UPLOAD_INVALID_TYPE',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// ── INTEGRAÇÕES — CATÁLOGO DE CAPACIDADES (Sprint 3.8.1) ────────────────────
// Correção arquitetural do módulo de Integrações de Portais: nenhuma tela ou
// service deve ter `if (provider === 'chaves_na_mao')` — toda decisão de
// "que campo mostrar"/"que operação permitir" é resolvida checando a
// capacidade do provider aqui, não o nome dele. Adicionar um portal novo é
// SÓ isto: um valor no enum IntegracaoProvider (schema.prisma) + uma linha
// no mapa abaixo — zero arquivo novo, zero componente novo.
export const INTEGRACAO_CAPACIDADES = {
  FEED_XML: 'feed_xml',
  API_ENDPOINT: 'api_endpoint',
  WEBHOOK_LEADS: 'webhook_leads',
  OAUTH: 'oauth',
  API_KEY: 'api_key',
  SECRET: 'secret',
  TOKEN: 'token',
} as const;

export type IntegracaoCapacidade = (typeof INTEGRACAO_CAPACIDADES)[keyof typeof INTEGRACAO_CAPACIDADES];

// Todo portal imobiliário hoje cadastrado pode ser configurado em qualquer
// um dos dois modelos oficiais (feed_xml OU api_endpoint, escolha do
// usuário na tela, não fixa em código) + recebe leads via webhook — por
// isso os 9 compartilham o mesmo conjunto. Um portal futuro com um
// contrato mais restrito (ex.: só API, sem feed) ganha um array diferente
// aqui, sem tocar em nenhum outro arquivo.
const CAPACIDADES_PORTAL_PADRAO: IntegracaoCapacidade[] = [
  INTEGRACAO_CAPACIDADES.FEED_XML,
  INTEGRACAO_CAPACIDADES.API_ENDPOINT,
  INTEGRACAO_CAPACIDADES.WEBHOOK_LEADS,
  INTEGRACAO_CAPACIDADES.API_KEY,
  INTEGRACAO_CAPACIDADES.SECRET,
  INTEGRACAO_CAPACIDADES.TOKEN,
];

const PROVIDERS_PORTAL = [
  'sub100',
  'jetimob',
  'grupo_zap',
  'viva_real',
  'olx',
  'facebook_marketplace',
  'chaves_na_mao',
  'site_proprio',
  'imovelweb',
] as const;

export const CAPACIDADES_POR_PROVIDER: Record<string, IntegracaoCapacidade[]> = {
  zapsign: [INTEGRACAO_CAPACIDADES.API_KEY],
  google_workspace: [INTEGRACAO_CAPACIDADES.OAUTH],
  microsoft365: [INTEGRACAO_CAPACIDADES.OAUTH],
  ...Object.fromEntries(PROVIDERS_PORTAL.map((provider) => [provider, CAPACIDADES_PORTAL_PADRAO])),
};

export function temCapacidadeIntegracao(provider: string, capacidade: IntegracaoCapacidade): boolean {
  return (CAPACIDADES_POR_PROVIDER[provider] ?? []).includes(capacidade);
}

// Um provider "de portal" é qualquer um que ofereça pelo menos um dos dois
// modelos de publicação — é essa checagem (não uma lista de nomes) que
// decide se a UI mostra as seções de Publicação/Leads/Diagnóstico.
export function ehProviderPortal(provider: string): boolean {
  return (
    temCapacidadeIntegracao(provider, INTEGRACAO_CAPACIDADES.FEED_XML) ||
    temCapacidadeIntegracao(provider, INTEGRACAO_CAPACIDADES.API_ENDPOINT)
  );
}
